<template>
  <div class="BillingPaymentContent">
    <div class="BillingPaymentContent-content">
      <div class="BillingPaymentContent-column">
        <!-- Current Plan Section -->
        <div class="BillingPaymentContent-currentPlan">
          <div class="BillingPaymentContent-sectionHeader">
            <span class="BillingPaymentContent-sectionHeaderLabel">
              {{ t('ui.billingPayment.details.currentPlan') }}:
            </span>
          </div>

          <div class="BillingPaymentContent-planContent">
            <Tag
              v-if="planName"
              size="sm"
              type="information"
              variant="outlined"
            >
              {{ t(`custom.plans.${planName}.title`) }}
            </Tag>

            <div class="BillingPaymentContent-priceRow">
              <span class="BillingPaymentContent-priceValue">{{ price }}</span>
              <span class="BillingPaymentContent-priceFrequency">
                /{{ t(`custom.plans.recurrency.${planFrequency}.short`) }}
              </span>
              <Tooltip v-if="showPriceTooltip" position="right">
                <template #trigger>
                  <Icon icon="information" class="BillingPaymentContent-triggerInfoIcon" />
                </template>

                <div class="Tooltip-description">
                  <p>{{ t('ui.subscription.details.tooltip.price') }}</p>
                </div>
              </Tooltip>
              <Tag
                v-if="promo?.isPromotional"
                size="xs"
                type="highlight"
                variant="filled"
                leftIcon="brightness-percent"
                class="BillingPaymentContent-promoTag"
              />
            </div>
          </div>
        </div>

        <!-- Billing Dates Section -->
        <div class="BillingPaymentContent-billingDates">
          <div class="BillingPaymentContent-billingRow">
            <span class="BillingPaymentContent-billingLabel">
              {{ t('ui.billingPayment.details.nextBillingDate') }}:
            </span>
            <span class="BillingPaymentContent-billingValue">{{ nextBillingDate }}</span>
          </div>

          <div
            v-if="commitmentEndDate"
            class="BillingPaymentContent-billingRow"
          >
            <span class="BillingPaymentContent-billingLabel">
              {{ t('ui.billingPayment.details.commitmentEndDate') }}:
            </span>
            <span class="BillingPaymentContent-billingValue">{{ commitmentEndDate }}</span>
          </div>

          <div
            v-if="showRetryButton"
            class="BillingPaymentContent-buttonItem"
          >
            <ActionButton
              size="sm"
              variant="primary"
              @click="handleRetryPayment"
            >
              Retry payment
            </ActionButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@cvo/i18n/composables/useI18n';
import type { CardDetails } from '@cvo/domain';
import Icon from '../../atoms/Icon/Icon.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import Tooltip from '../../atoms/Tooltip/Tooltip.vue';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';

export interface BillingPaymentContentProps {
  planName?: string;
  price: string;
  planFrequency?: string | null;
  nextBillingDate: string;
  commitmentEndDate?: string;
  cardDetails?: CardDetails;
  creditCardBrandIconUrl?: string;
  promo?: {
    isPromotional: boolean;
    isEvergreen: boolean;
    revertsAt?: string;
    fullPrice?: string;
  };
  showRetryButton?: boolean;
  showPriceTooltip?: boolean;
}

const props = withDefaults(defineProps<BillingPaymentContentProps>(), {
  showPriceTooltip: true,
});

const emit = defineEmits<{
  retryPayment: [];
}>();

const { t } = useI18n();

function handleRetryPayment() {
  emit('retryPayment');
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .BillingPaymentContent {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-10;
    padding: vars.$dimensions-d-4;
    background-color: vars.$organisms-card-contentcard-billing-default-plan-bg-color;
    border-radius: vars.$organisms-card-contentcard-billing-border-radius;

    @include responsive.mobile {
      gap: vars.$dimensions-d-4;
    }

    &-content {
      display: flex;
      gap: vars.$dimensions-d-12;

      @include responsive.mobile {
        flex-direction: column;
        gap: vars.$dimensions-d-8;
      }
    }

    &-column {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-5;
      flex: 1;
      min-inline-size: 0;

      &--right {
        align-items: flex-end;
      }
    }

    &-currentPlan {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
    }

    &-sectionHeader {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
      padding-block-end: vars.$dimensions-d-2;
      border-block-end: vars.$dimensions-d-0-25 solid vars.$organisms-card-contentcard-billing-divider-color;
    }

    &-sectionHeaderLabel {
      color: vars.$organisms-card-contentcard-billing-default-label-color;
      @include text.text('sm', $font-weight: vars.$font-weight-text-500);
      white-space: nowrap;
    }

    &-planContent {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: vars.$dimensions-d-2;
      padding-block-start: vars.$dimensions-d-2;
    }

    &-priceRow {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-1;
    }

    &-priceValue {
      color: vars.$organisms-card-contentcard-details-text-color;
      @include text.text('lg', $font-weight: vars.$font-weight-text-500);
    }

    &-priceFrequency {
      color: vars.$organisms-card-contentcard-details-text-color;
      @include text.text('sm');
    }

    &-triggerInfoIcon {
      color: vars.$organisms-card-contentcard-details-icon-info-color;
    }

    &-promoTag {
      padding: vars.$dimensions-d-1;
      min-width: auto;
    }

    &-billingDates {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
    }

    &-billingRow {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;

      &:first-child {
        padding-block-start: vars.$dimensions-d-2;
        border-block-start: vars.$dimensions-d-0-25 solid vars.$organisms-card-contentcard-billing-divider-color;
      }

      @include responsive.mobile {
        flex-direction: column;
        align-items: flex-start;
        gap: vars.$dimensions-d-1;
      }
    }

    &-billingLabel {
      color: vars.$organisms-card-contentcard-details-label-color;
      @include text.text('sm', $font-weight: vars.$font-weight-text-500);
      white-space: nowrap;
    }

    &-billingValue {
      color: vars.$organisms-card-contentcard-details-text-color;
      @include text.text('md');
      flex: 1;
      white-space: nowrap;
    }

    &-buttonItem {
      display: flex;
    }

    &-disclaimer {
      margin: 0;
      color: vars.$organisms-card-contentcard-details-disclaimer-color;
      @include text.text('xs');
    }
  }
}
</style>
